import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Popup, Dropdown, Button } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import ReactFlow, {
  removeElements,
  addEdge,
  MiniMap,
  Controls,
  Background,
  updateEdge,
} from 'react-flow-renderer';

import DeviceNode from './DeviceNode';
import PowerNodeContainer from '../../containers/PowerNodeContainer';
import LineNodeContainer from '../../containers/LineNodeContainer';
import LineLastNodeContainer from '../../containers/LineLastNodeContainer';
import DeviceAdd from './DeviceAdd';
import { ReactComponent as PlusMathIcon } from '../../assets/images/plus-math-icon.svg';

import CustomEdgeContainer from '../../containers/CustomEdgeContainer';
import styles from './Diagram.module.scss';

const edgeTypes = {
  custom: CustomEdgeContainer,
};

const nodeTypes = {
  deviceNode: DeviceNode,
  powerNode: PowerNodeContainer,
  lineNode: LineNodeContainer,
  lineLastNode: LineLastNodeContainer,
};

const Diagram = React.memo(
  ({
    nextId,
    factories,
    items,
    devices,
    isEditable,
    onCreateDeviceDiagram,
    onUpdateDiagram,
    onFetchDiagram,
  }) => {
    const [t] = useTranslation();
    const onLoad = (reactFlowInstance) => reactFlowInstance.fitView();
    const onNodeDragStop = (event, node) => {
      const dataNode = node;
      const { id } = dataNode;
      delete dataNode.id;
      const newData = {
        id,
        diagram: dataNode,
      };
      onUpdateDiagram(newData);
    };

    const [elements, setElements] = useState([]);
    const [currentFactory, setCurrentFactory] = useState(
      (factories.length > 0 && factories[0]?.id) || 0,
    );
    const [isAddDeviceOpened, setIsAddDeviceOpened] = useState(false);

    const onElementsRemove = (elementsToRemove) =>
      setElements((els) => removeElements(elementsToRemove, els));

    const handleAddDeviceClick = useCallback(() => {
      setIsAddDeviceOpened(true);
    }, []);

    const handleAddDeviceClose = useCallback(() => {
      setIsAddDeviceOpened(false);
    }, []);

    const handleCreateDeviceDiagram = useCallback(
      (data) => {
        const newData = {
          factoryId: currentFactory,
          name: 'Diagram',
          diagram: {
            type: 'deviceNode',
            data: { id: data.id },
            position: { x: 300, y: 100 },
          },
        };
        onCreateDeviceDiagram(newData);
      },
      [currentFactory, onCreateDeviceDiagram],
    );

    useEffect(() => {
      const diagramData = [];
      items.forEach((item) => {
        diagramData.push({
          id: item.id,
          ...item.diagram,
        });
      });
      setElements(diagramData);
    }, [items]);

    const onEdgeUpdate = (oldEdge, newConnection) => {
      onUpdateDiagram({
        id: oldEdge.id,
        diagram: {
          ...newConnection,
          type: 'custom',
        },
      });
      setElements((els) => updateEdge(oldEdge, newConnection, els));
    };

    const onConnect = (params) =>
      setElements((els) => {
        const newData = {
          factoryId: currentFactory,
          name: 'Diagram',
          diagram: {
            ...params,
            type: 'custom',
          },
        };
        onCreateDeviceDiagram(newData);
        addEdge(params, els);
      });

    const factoryOptions = [];
    factories.forEach((ft) => {
      factoryOptions.push({ key: ft.id, value: ft.id, text: ft.name });
    });

    const handleChangeFactory = useCallback(
      (event, data) => {
        setCurrentFactory(data.value);
        onFetchDiagram(data.value);
      },
      [onFetchDiagram],
    );

    const handleAddPowweClick = useCallback(() => {
      const newData = {
        factoryId: currentFactory,
        name: 'Diagram',
        diagram: {
          type: 'powerNode',
          data: { id: `power${nextId}` },
          position: { x: 300, y: 100 },
        },
      };
      onCreateDeviceDiagram(newData);
    }, [nextId, currentFactory, onCreateDeviceDiagram]);

    const handleAddLineClick = useCallback(() => {
      const newData = {
        factoryId: currentFactory,
        name: 'Diagram',
        diagram: {
          type: 'lineNode',
          data: { id: nextId },
          position: { x: 300, y: 100 },
        },
      };
      onCreateDeviceDiagram(newData);
    }, [nextId, currentFactory, onCreateDeviceDiagram]);

    const handleAddLineLastClick = useCallback(() => {
      const newData = {
        factoryId: currentFactory,
        name: 'Diagram',
        diagram: {
          type: 'lineLastNode',
          data: { id: nextId },
          position: { x: 300, y: 100 },
        },
      };
      onCreateDeviceDiagram(newData);
    }, [nextId, currentFactory, onCreateDeviceDiagram]);

    return (
      <div className={styles.wrapper}>
        <div className={styles.diagramActions}>
          <Dropdown
            placeholder={t('common.selectFactory')}
            selection
            options={factoryOptions}
            onChange={handleChangeFactory}
            value={currentFactory}
          />
          {isEditable && (
            <div className={styles.groupButtonAdd}>
              <Button
                type="button"
                className={styles.addDeviceButton}
                onClick={handleAddPowweClick}
              >
                <PlusMathIcon className={styles.addDeviceButtonIcon} />
                <span className={styles.addDeviceButtonText}>{t('action.addPower')}</span>
              </Button>
              <Button type="button" className={styles.addDeviceButton} onClick={handleAddLineClick}>
                <PlusMathIcon className={styles.addDeviceButtonIcon} />
                <span className={styles.addDeviceButtonText}>{t('action.addLine')}</span>
              </Button>
              <Button
                type="button"
                className={styles.addDeviceButton}
                onClick={handleAddLineLastClick}
              >
                <PlusMathIcon className={styles.addDeviceButtonIcon} />
                <span className={styles.addDeviceButtonText}>{t('action.addLineLast')}</span>
              </Button>
              <Popup
                basic
                position="top center"
                open={isAddDeviceOpened}
                trigger={
                  <Button
                    type="button"
                    className={styles.addDeviceButton}
                    onClick={handleAddDeviceClick}
                  >
                    <PlusMathIcon className={styles.addDeviceButtonIcon} />
                    <span className={styles.addDeviceButtonText}>
                      {devices.length > 0 ? t('action.addAnotherDevice') : t('action.addDevice')}
                    </span>
                  </Button>
                }
                content={
                  <DeviceAdd
                    devices={devices}
                    isOpened={isAddDeviceOpened}
                    onCreate={handleCreateDeviceDiagram}
                    onClose={handleAddDeviceClose}
                  />
                }
                on="click"
                hideOnScroll
                className={styles.popupAddDevice}
              />
            </div>
          )}
        </div>
        <ReactFlow
          elements={elements}
          onElementsRemove={onElementsRemove}
          onNodeDragStop={onNodeDragStop}
          onLoad={onLoad}
          snapToGrid
          edgeTypes={edgeTypes}
          key="edges"
          nodeTypes={nodeTypes}
          onEdgeUpdate={onEdgeUpdate}
          onConnect={onConnect}
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    );
  },
);

Diagram.propTypes = {
  nextId: PropTypes.string.isRequired,
  factories: PropTypes.arrayOf(PropTypes.any).isRequired,
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  devices: PropTypes.arrayOf(PropTypes.any).isRequired,
  isEditable: PropTypes.bool.isRequired,
  onCreateDeviceDiagram: PropTypes.func.isRequired,
  onUpdateDiagram: PropTypes.func.isRequired,
  onFetchDiagram: PropTypes.func.isRequired,
};

export default Diagram;
